import React, { FormEventHandler } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { Link as RouterLink } from "react-router-dom";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";

import { useState, useCallback } from "react";
import { useAuthSession } from "../../components/AuthSessionProvider";
import { useNotify } from "../../components/@common/NotificationsProvider";
import { useLanguageTranslator } from "../../components";
import { EyeCloseIcon, EyeOpenIcon } from "../../@lib/icons";
import { IconButton, InputAdornment } from "@mui/material";
import I18nSelect from "../../components/@common/I18nSelect";

const Login: React.FC = () => {
  const notify = useNotify();
  const { loginUser } = useAuthSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const { translator } = useLanguageTranslator();

  /**
   * handleSignin()
   */
  const handleSignin: FormEventHandler<HTMLFormElement> = useCallback(
    async (ev) => {
      try {
        ev.preventDefault();

        setSigningIn(true);
        await loginUser(email, password);
      } catch (err) {
        notify.error(err as Error);
        setSigningIn(false);
      }
    },
    [email, password, loginUser, notify]
  );

  const isFormEmpty = !email.trim() || !password.trim();

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Stack sx={{ height: "100%" }} justifyContent="center">
      <Container maxWidth="xs">
        <Box mb={4}>
          <Typography variant="h3" align="center" gutterBottom>
            {translator("Welcome")}
          </Typography>
          <Typography align="center">
            {translator("Welcome")}!{" "}
            {translator("Please enter your credentials")}. {}
          </Typography>
        </Box>

        <Stack component="form" spacing={1.5} onSubmit={handleSignin}>
          <FormControl disabled={signingIn}>
            <FormLabel htmlFor="email">{translator(`generic.Email`)}</FormLabel>
            <OutlinedInput
              id="email"
              name="email"
              type="email"
              placeholder={translator(`generic.Email`)}
              autoFocus
              required
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </FormControl>

          <FormControl disabled={signingIn}>
            <FormLabel htmlFor="password">
              {translator(`generic.Password`)}
            </FormLabel>
            <OutlinedInput
              id="password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              placeholder={translator(`generic.Password`)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {showPassword ? <EyeOpenIcon /> : <EyeCloseIcon />}
                  </IconButton>
                </InputAdornment>
              }
              onChange={(ev) => setPassword(ev.target.value)}
            />
          </FormControl>

          <Stack direction="row" alignItems="center" justifyContent="end">
            {/* <FormControlLabel
              label={translator("Remember for 30 days")}
              control={<Checkbox size="small" />}
            /> */}

            <Link
              component={RouterLink}
              to="/auth/forgot-password"
              tabIndex={-1}
              color="inherit"
              underline="hover"
              variant="body2"
              fontWeight={500}
            >
              {translator("generic.Forgot Password")} ?
            </Link>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isFormEmpty || signingIn}
          >
            {translator("Sign in")}
          </Button>
        </Stack>

        <Stack direction="row" alignItems="end" mt={2} justifyContent="center">
          <I18nSelect
            options={[
              { label: translator("English"), value: "en" },
              { label: translator("French"), value: "fr" },
              { label: translator("Portuguese"), value: "pt" },
            ]}
          />
        </Stack>
        <center style={{ marginTop: 32 }}>
          <Link
            href="/"
            tabIndex={-1}
            color="textSecondary"
            variant="caption"
            fontWeight={500}
          >
            {translator("Back to Home")}
          </Link>
        </center>
      </Container>
    </Stack>
  );
};

export default Login;
